// Defines the <newsletter-card> teaser and the global <newsletter-modal>. This
// file is loaded from both the footer (which renders the one global modal) and
// the newsletter-card section, so it's wrapped in an IIFE and the element
// definitions are guarded — running it twice on a page is harmless.
(function () {
// Set once a visitor completes signup; from then on the card stays hidden on
// this browser. Written by NewsletterModal, read by NewsletterCard.maybeOpen.
const NEWSLETTER_SUBSCRIBED_KEY = 'newsletterCardSubscribed';

class NewsletterCard extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.reopenHours = parseInt(this.dataset.reopenHours, 10) || 24;
    this.storageKey = 'newsletterCardClosed';
  }

  connectedCallback() {
    /* Escape ancestor stacking contexts by re-parenting to <body> (AAS-4640).
       The card is position:fixed at z-index 50, but on a PDP it renders inside a
       section carrying position:relative;z-index:3 — every section after the
       About block gets that from the ski hero morph (section-product-about.css).
       Confined to z:3 it can never reach .product__actions-row, which is fixed
       at the SAME z-index 50 but sits in the root context, so the actions row
       painted over the card. Both use --z-pdp-sticky-bar, so raising the card's
       z-index would not have helped: the ancestor was the ceiling.

       Same approach pdp-gallery-apparel-overlay.js takes for its backdrop.
       Moving a node re-runs connectedCallback, so this returns and lets the
       second pass do the real init — otherwise every listener binds twice. */
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.closeButton = this.querySelector('.newsletter-card__close');
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.close.bind(this));
    }

    // The "Sign up" button opens the signup modal for this same section.
    this.signupButton = this.querySelector('.newsletter-card__signup');
    if (this.signupButton) {
      this.signupButton.addEventListener('click', () => this.openModal());
    }

    // Inline "sign up" links in the rich text (placeholder href="#") open the
    // modal too. Real URLs the merchant sets are left to navigate normally.
    this.querySelectorAll('.newsletter-card__content a[href="#"], .newsletter-card__content a[href$="#"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.openModal();
      });
    });

    this.maybeOpen();

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.sectionId) this.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.sectionId) this.removeAttribute('open');
      });
    }
  }

  maybeOpen() {
    // Always show inside the theme editor so merchants can style it.
    if (window.Shopify && Shopify.designMode) {
      this.open();
      return;
    }

    // localStorage can throw (e.g. private mode); if we can't read the flags,
    // fall through and show the card.
    try {
      // Don't show to anyone who has already signed up on this browser.
      if (localStorage.getItem(NEWSLETTER_SUBSCRIBED_KEY)) return;

      const closedUntil = parseInt(localStorage.getItem(this.storageKey), 10);
      if (closedUntil && new Date().getTime() < closedUntil) return;
    } catch (e) {
      /* localStorage unavailable — ignore and show the card. */
    }

    this.openWhenConsentSettled();
  }

  /* AAS-4698: the card used to open the instant the page loaded — on a first
   * visit that is exactly when the OneTrust banner holds the user's
   * attention, so the entrance choreography played unseen (and competed with
   * the consent choice). Open only once consent is settled:
   *   1. answered in a previous session → OptanonAlertBoxClosed cookie exists;
   *   2. answered now → the OneTrust bridge pushes the choice into Shopify's
   *      Customer Privacy API, which fires `visitorConsentCollected`;
   *   3. no banner ever appears (OneTrust absent or blocked) → open after a
   *      short grace poll finds no visible banner. */
  openWhenConsentSettled() {
    if (document.cookie.indexOf('OptanonAlertBoxClosed=') > -1) {
      this.open();
      return;
    }

    let settled = false;
    const openOnce = () => {
      if (settled) return;
      settled = true;
      clearInterval(gracePoll);
      this.open();
    };

    document.addEventListener('visitorConsentCollected', openOnce, { once: true });

    let quietTicks = 0;
    const gracePoll = setInterval(() => {
      if (settled) { clearInterval(gracePoll); return; }
      const banner = document.getElementById('onetrust-banner-sdk');
      const bannerVisible = banner && banner.offsetParent !== null;
      quietTicks = bannerVisible ? 0 : quietTicks + 1;
      // ~4s with no banner on screen → nothing is obstructing, open.
      if (quietTicks >= 4) openOnce();
    }, 1000);
  }

  open() {
    this.setAttribute('open', '');
  }

  openModal() {
    // One global modal, rendered by the footer; open the first one on the page.
    const modal = document.querySelector('newsletter-modal');
    if (modal && typeof modal.open === 'function') modal.open();
  }

  close() {
    /* AAS-4698: the entrance choreography plays in REVERSE before the card
     * hides — [closing] (with [open] kept, so display stays block) drives
     * the mirrored CSS timeline (corner-anchored on desktop, bottom-anchored
     * on mobile); the attributes drop when it ends. Only
     * prefers-reduced-motion keeps the instant hide. */
    const animateOut =
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (animateOut && !this._closingTimer) {
      this.setAttribute('closing', '');
      this._closingTimer = setTimeout(() => {
        this._closingTimer = null;
        this.removeAttribute('open');
        this.removeAttribute('closing');
      }, 1100); /* matches the ~1s exit timeline in section-newsletter-card.css */
    } else if (!animateOut) {
      this.removeAttribute('open');
    }

    // Don't persist dismissal while editing in the theme editor.
    if (window.Shopify && Shopify.designMode) return;

    const reopenAt = new Date().getTime() + this.reopenHours * 60 * 60 * 1000;
    try {
      localStorage.setItem(this.storageKey, reopenAt);
    } catch (e) {
      /* localStorage unavailable (e.g. private mode) — ignore. */
    }
  }
}

if (!customElements.get('newsletter-card')) {
  customElements.define('newsletter-card', NewsletterCard);
}

class NewsletterModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.newsletter-modal__dialog');
    this.form = this.querySelector('.newsletter-modal__form');
    this.message = this.querySelector('.newsletter-modal__message');
    this.submitButton = this.querySelector('.newsletter-modal__submit');
    this.submitLabel = this.submitButton ? this.submitButton.textContent.trim() : 'Sign up';

    this.querySelectorAll('[data-newsletter-close]').forEach((el) => {
      el.addEventListener('click', this.close.bind(this));
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') this.close();
    };

    // Keep selects black once a real value is chosen (placeholder stays grey).
    this.querySelectorAll('.newsletter-modal__field--select select').forEach((select) => {
      select.addEventListener('change', () => this.syncSelects());
    });
    this.syncSelects();

    if (this.form) this.form.addEventListener('submit', this.submit.bind(this));

    // The dialling code lives in the select and ONLY there — it is never written
    // into the text field, or the visitor sees "+358" twice. The field holds the
    // national number alone, and the two are joined at submit.
    //
    // Keyboard input is restricted to digits so a second prefix cannot be typed
    // in: "+358" in the select plus "+358 12345" typed would otherwise produce
    // "+358+35812345". Paste and autofill go through the same filter.
    const phoneInput = this.form && this.form.elements.phone;
    if (phoneInput) {
      phoneInput.addEventListener('input', () => {
        const digits = phoneInput.value.replace(/\D/g, '');
        if (digits !== phoneInput.value) {
          const at = phoneInput.selectionStart;
          const removed = phoneInput.value.length - digits.length;
          phoneInput.value = digits;
          // Keep the caret where the visitor was, minus whatever was stripped.
          try { phoneInput.setSelectionRange(Math.max(0, at - removed), Math.max(0, at - removed)); } catch (e) {}
        }
      });
    }
  }

  // The value the CRM receives: "+45 123123123" — dialling code, one space, then
  // the national digits. The space is what the old platform stored
  // ("+44 2342342343") and it keeps the code readable in CRM screens.
  // Returns '' when no number was entered, so an untouched field sends nothing.
  phoneValue() {
    const el = this.form && this.form.elements;
    if (!el) return '';
    const national = (el.phone ? el.phone.value : '').replace(/\D/g, '');
    if (!national) return '';
    const prefix = (el.phone_prefix ? el.phone_prefix.value : '').trim();
    return prefix ? `${prefix} ${national}` : national;
  }


  // A select shows its placeholder greyed out until it holds a real value.
  // Call after anything that changes a select's value from script (prefill, reset).
  syncSelects() {
    this.querySelectorAll('.newsletter-modal__field--select').forEach((field) => {
      const select = field.querySelector('select');
      field.classList.toggle('is-filled', !!(select && select.value));
    });
  }

  // Seed the form from another section (e.g. the editorial stage email field)
  // before opening: the email already typed, the storefront country, and any
  // author-configured interests that match options shown here.
  prefill(values) {
    if (!this.form || !values) return;

    if (values.email) {
      const email = this.form.elements.email;
      if (email) email.value = values.email;
    }

    if (values.countryCode) {
      const country = this.form.elements.country_code;
      if (country && Array.from(country.options).some((o) => o.value === values.countryCode)) {
        country.value = values.countryCode;
        const wrap = country.closest('.newsletter-modal__field--select');
        if (wrap) wrap.classList.add('is-filled');
      }
    }

    if (Array.isArray(values.interests) && values.interests.length) {
      const wanted = new Set(values.interests);
      this.form.querySelectorAll('input[name="interests"]').forEach((box) => {
        box.checked = wanted.has(box.value);
      });
    }
  }

  open() {
    // Bottom-sheet base (.info-drawer): the element is always in layout but
    // hidden via opacity/visibility, so flipping aria-hidden to "false" runs the
    // slide-up transition (translateY 100% → 0) — the same activation contract
    // every other info-drawer uses. See info-drawer.css.
    this.setAttribute('aria-hidden', 'false');
    window.ScrollLock ? ScrollLock.lock('newsletter-card') : (document.body.style.overflow = 'hidden');
    document.addEventListener('keydown', this.onKeydown);
    const firstField = this.querySelector('input, select');
    if (firstField) firstField.focus();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    window.ScrollLock ? ScrollLock.unlock('newsletter-card') : (document.body.style.overflow = '');
    document.removeEventListener('keydown', this.onKeydown);
  }

  setMessage(text, type) {
    if (!this.message) return;
    this.message.textContent = text || '';
    this.message.hidden = !text;
    this.message.classList.toggle('newsletter-modal__message--error', type === 'error');
    this.message.classList.toggle('newsletter-modal__message--success', type === 'success');
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.textContent = isLoading
      ? (window.newsletterModalStrings && window.newsletterModalStrings.loading) || 'Signing up…'
      : this.submitLabel;
  }

  // Human label for a field, taken from its placeholder (already localized) with
  // the trailing required "*" stripped — used to name the field in the summary.
  fieldLabel(input) {
    if (!input) return '';
    return (input.placeholder || input.name || '').replace(/\*+\s*$/, '').trim();
  }

  // Drop every red error mark left by a previous failed submit.
  clearErrors() {
    if (!this.form) return;
    this.form
      .querySelectorAll(
        '.newsletter-modal__field--error, .newsletter-modal__group--error, .newsletter-modal__check--error'
      )
      .forEach((el) =>
        el.classList.remove(
          'newsletter-modal__field--error',
          'newsletter-modal__group--error',
          'newsletter-modal__check--error'
        )
      );
  }

  // Validate the whole form at once: flag every invalid control red, show one
  // message naming what needs attention, and focus the first problem. Returns
  // true only when nothing is wrong. The <form> is novalidate, so this fully
  // owns validation — the native bubble can't anchor to the visually-hidden
  // consent/channel checkboxes, which is why an unticked box used to block the
  // submit silently with no message and nothing turning red.
  validate(data, strings) {
    this.clearErrors();
    const problems = [];
    let firstInvalid = null;

    // Flag one control: add the error class to its container and wire a one-time
    // clear so the red disappears the moment the visitor fixes it.
    const flag = (input, container, cls) => {
      if (!input) return;
      const el = input.closest(container) || input;
      el.classList.add(cls);
      const clear = () => el.classList.remove(cls);
      input.addEventListener('input', clear, { once: true });
      input.addEventListener('change', clear, { once: true });
      if (!firstInvalid) firstInvalid = input;
    };

    // Required text fields — named from their placeholder so the summary says
    // exactly which one is missing.
    ['first_name', 'last_name'].forEach((name) => {
      const input = this.form.elements[name];
      if (input && !(data.get(name) || '').toString().trim()) {
        flag(input, '.newsletter-modal__field', 'newsletter-modal__field--error');
        problems.push(this.fieldLabel(input));
      }
    });

    // Email — required and well-formed.
    const email = (data.get('email') || '').toString().trim();
    const emailInput = this.form.elements.email;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      flag(emailInput, '.newsletter-modal__field', 'newsletter-modal__field--error');
      problems.push(this.fieldLabel(emailInput));
    }

    // At least one "stay updated via" channel (contract requires one).
    if (!data.getAll('channels').filter(Boolean).length) {
      const box = this.form.querySelector('input[name="channels"]');
      flag(box, '.newsletter-modal__group', 'newsletter-modal__group--error');
      const group = box && box.closest('.newsletter-modal__group');
      const legend = group && group.querySelector('.newsletter-modal__group-title');
      problems.push(legend ? legend.textContent.replace(/\*+\s*$/, '').trim() : strings.channelLabel || 'Stay updated via');
    }

    // Terms consent — only present when the author set the text.
    const termsBox = this.form.elements.terms;
    if (termsBox && !termsBox.checked) {
      flag(termsBox, '.newsletter-modal__check', 'newsletter-modal__check--error');
      problems.push(strings.termsLabel || 'Terms and conditions');
    }

    // Birthdate is optional, but must be well-formed when given (the date spinner
    // otherwise accepts years like 99999).
    const birthdate = (data.get('birthdate') || '').toString();
    if (birthdate && !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      flag(this.form.elements.birthdate, '.newsletter-modal__field', 'newsletter-modal__field--error');
      problems.push(strings.birthdateLabel || 'Date of birth');
    }

    // Phone is optional. When a number IS given it must be international — it
    // feeds the CRM's SMS channel, and a national number cannot be dialled from
    // there. So a number with no dialling code selected is an error, and the
    // combined value must be E.164: +, a non-zero country digit, then 7-14 more.
    const nationalDigits = (data.get('phone') || '').toString().replace(/\D/g, '');
    if (nationalDigits) {
      // Validate the compact form; the sent value carries a space for legibility.
      const combined = this.phoneValue().replace(/\s+/g, '');
      if (!/^\+[1-9]\d{6,14}$/.test(combined)) {
        // Flag the select when the code is what's missing, else the number.
        const missingCode = !(data.get('phone_prefix') || '').toString().trim();
        const target = missingCode ? this.form.elements.phone_prefix : this.form.elements.phone;
        flag(target, '.newsletter-modal__field', 'newsletter-modal__field--error');
        problems.push(strings.phoneLabel || this.fieldLabel(this.form.elements.phone) || 'Phone');
      }
    }

    if (!problems.length) {
      this.setMessage('', null);
      return true;
    }

    const lead = strings.fixFields || 'Please check the highlighted fields:';
    this.setMessage(`${lead} ${problems.join(', ')}.`, 'error');
    if (firstInvalid && typeof firstInvalid.focus === 'function') firstInvalid.focus();
    return false;
  }

  async submit(event) {
    event.preventDefault();

    const strings = window.newsletterModalStrings || {};
    const data = new FormData(this.form);

    // If we're coming from the post-success "Thank you" button, drop that state
    // so this submit renders normally (label → loading → thank you again).
    if (this.submitButton) {
      this.submitButton.classList.remove('newsletter-modal__submit--success');
      this.submitButton.textContent = this.submitLabel;
    }

    if (!this.validate(data, strings)) return;

    if (!window.AtomicApi) {
      this.setMessage(strings.error || 'Something went wrong. Please try again in a moment.', 'error');
      return;
    }

    const email = (data.get('email') || '').toString().trim();

    // `country` drives the subscription region. Prefer the chosen country,
    // fall back to the storefront's localized market so the call always carries one.
    const marketCountry = (window.theme && window.theme.market && window.theme.market.country_code) || '';
    const country = (data.get('country_code') || '').toString() || marketCountry;

    // `language` is the same kind of fact as `country` and comes from the same
    // place — the storefront the visitor is browsing. There is no form field for
    // it, so it is always the market's. Uppercased ("CS") to match the CRM.
    const language = (window.theme && window.theme.market && window.theme.market.language_code) || '';

    // Selected interests map straight to the CRM `interests[]` array (e.g.
    // ["Atomic_piste", "Atomic_freeride"]). They are no longer folded into a `code` string.
    const interests = data.getAll('interests').map((i) => i.toString());

    // `origin` tells the CRM which touchpoint produced the signup. The value is
    // configured in the theme editor (newsletter-card section → "CRM origin",
    // default "ecom_NewsletterPage") and reaches us as data-crm-origin on
    // <newsletter-modal> — see snippets/newsletter-modal.liquid. There is
    // deliberately NO fallback here: the CRM-agreed value belongs in the editor
    // alone, so a change never needs a theme deploy and the two can never
    // disagree. Clearing the setting therefore sends an empty origin.
    const origin = (this.dataset.crmOrigin || '').trim();

    // The channel checkboxes become the CRM `newsletters` array. Each checkbox's
    // value IS the CRM code (e.g. Atomic_Email), straight from the selected
    // newsletter_channel_list metaobject. validate() already guaranteed one.
    // Every entry carries the same `origin`.
    const newsletters = data
      .getAll('channels')
      .map((code) => code.toString())
      .filter(Boolean)
      .map((code) => ({ code, subscribed: true, origin }));

    // The CRM only accepts M / F / O for gender. Map the select values; leave
    // empty (and omit below) when the visitor didn't pick one — gender is optional.
    const GENDER_CODES = { female: 'F', male: 'M', other: 'O' };
    const gender = GENDER_CODES[(data.get('gender') || '').toString()] || '';

    // Native date input — its value is already YYYY-MM-DD (the CRM format) or
    // empty; validate() has already rejected any malformed value.
    const birthdate = (data.get('birthdate') || '').toString();

    const payload = {
      email,
      firstName: (data.get('first_name') || '').toString().trim(),
      lastName: (data.get('last_name') || '').toString().trim(),
      phone: this.phoneValue(),
      country,
      language,
      birthdate,
      interests,
      newsletters,
    };

    // Only send gender when it maps to a valid CRM code; the server rejects
    // anything that isn't M / F / O (including an empty string).
    if (gender) payload.gender = gender;

    this.setLoading(true);
    this.setMessage('', null);

    try {
      // The CRM newsletter endpoint strictly rejects unknown keys, so opt out
      // of the client's automatic `locale` injection.
      const res = await window.AtomicApi.post('/customers/newsletter', payload, { includeLocale: false });
      if (res && res.ok) {
        // showThankYou takes over the button (disabled + "Thank you"); don't let
        // the setLoading(false) below run and reset it.
        this.showThankYou(strings);
        return;
      }
      this.setMessage((res && res.error && res.error.message) || strings.error || 'Something went wrong. Please try again.', 'error');
    } catch (e) {
      // Network failure / thrown error — never leave the button stuck loading.
      this.setMessage(strings.error || 'Something went wrong. Please try again.', 'error');
    }

    this.setLoading(false);
  }

  // Clear the form once a signup has gone through, so a stale set of personal
  // details isn't left sitting in the modal (AAS-4273). The email is deliberately
  // put back: it's the one field that confirms *which* address was signed up.
  resetForm() {
    if (!this.form) return;

    // reset() returns the form to its rendered defaults: every text field empty
    // (email included — cleared after a successful signup), the first interest
    // and channel checked again, country back to the storefront's market, and
    // terms unticked.
    this.form.reset();

    // The birthdate field starts as text and flips to a native date picker on
    // focus. Now that it's empty, put it back so its placeholder shows again.
    const birthdate = this.form.elements.birthdate;
    if (birthdate && !birthdate.value) birthdate.type = 'text';

    this.clearErrors();
    this.syncSelects();
  }

  // Success state: the submit button itself becomes the "Thank you" confirmation
  // (per the Figma design) rather than showing a separate message.
  showThankYou(strings) {
    this.setMessage('', null);
    this.resetForm();

    // dataLayer (GTM): newsletter signup completed. This was previously pushed
    // from the footer's inline-form success render; signup now completes in this
    // modal, so the event fires here. Harmless when GTM isn't loaded — dataLayer
    // is just an array nothing reads.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'newsletter_subscription_completed' });

    // Remember the signup so the teaser card won't reappear on this browser,
    // and dismiss it now if it's still showing behind the modal.
    if (!(window.Shopify && Shopify.designMode)) {
      try {
        localStorage.setItem(NEWSLETTER_SUBSCRIBED_KEY, '1');
      } catch (e) {
        /* localStorage unavailable (e.g. private mode) — ignore. */
      }
    }
    const card = document.querySelector('newsletter-card');
    if (card) card.removeAttribute('open');

    if (!this.submitButton) return;
    // Keep the button live so the (now-reset) form can be used again — clicking
    // the "Thank you" button re-submits, and a fresh submit() clears this success
    // state back to the normal label first.
    this.submitButton.disabled = false;
    this.submitButton.textContent = (strings && strings.thankYou) || 'Thank you';
    this.submitButton.classList.add('newsletter-modal__submit--success');
  }
}

if (!customElements.get('newsletter-modal')) {
  customElements.define('newsletter-modal', NewsletterModal);
}
})();
